import { initializeApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  OAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

const useEmulators = import.meta.env.VITE_USE_EMULATORS === '1';
const projectId = import.meta.env.VITE_FB_PROJECT_ID || 'demo-hub';
const isDemoProject = projectId.startsWith('demo-');

// Fail fast on the two dangerous misconfigurations (Codex slice-0 review #3):
if (!useEmulators && isDemoProject) {
  throw new Error(
    `Refusing to start: VITE_USE_EMULATORS=0 but project is "${projectId}". ` +
      'Set a real VITE_FB_PROJECT_ID (+ api key) in .env.production, or run with emulators.',
  );
}
if (useEmulators && !isDemoProject) {
  console.warn(
    `[cockpit] emulator mode against a non-demo project "${projectId}" - ` +
      'double-check you are not pointing dev config at a real backend.',
  );
}

const app = initializeApp({
  projectId,
  apiKey: import.meta.env.VITE_FB_API_KEY || 'demo-key',
  // Popup sign-in needs an authDomain even against the auth emulator.
  authDomain: import.meta.env.VITE_FB_AUTH_DOMAIN || (useEmulators ? `${projectId}.firebaseapp.com` : undefined),
  appId: import.meta.env.VITE_FB_APP_ID || undefined,
});

export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');

if (useEmulators) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

/** Microsoft / Entra sign-in, tenant-locked. */
export function microsoftProvider() {
  const p = new OAuthProvider('microsoft.com');
  const tenant = import.meta.env.VITE_ENTRA_TENANT_ID;
  if (tenant) p.setCustomParameters({ tenant });
  return p;
}

export function signInMicrosoft() {
  return signInWithPopup(auth, microsoftProvider());
}

export function signOut() {
  return fbSignOut(auth);
}

export const EMULATOR_MODE = useEmulators;
